import React, { useEffect, useMemo, useRef, useState } from "react"

import InputTag from "../Input/InputTag"
import { debounce, throttle } from "../../utils/utils"
import { DropdownObj } from "../../pages/Common/commonTypes"

import downArrow from "../../assets/images/down_arrow.svg"
import styles from "./VirtualizedSearchSelect.module.scss"
import { Box, Popover } from "@mui/material"

type Props = {
    options: DropdownObj[]
    selectedOption?: DropdownObj | null
    onSearch: (searchTerm: string) => void
    onLoadMore: () => void
    onChange?: (value: DropdownObj) => void
    placeholder?: string
    height?: string
    className?: string
    loading?: boolean
    showError?: boolean
    error?: string
    fontSize?: string
    disabled?: boolean

    multiSelect?: boolean
    selectedOptions?: DropdownObj[]
    onMultiChange?: (values: DropdownObj[]) => void
}

const VirtualizedSearchSelect = (props: Props) => {
    const [showOptions, setShowOptions] = useState(false)
    const [selectedOption, setSelectedOption] = useState<DropdownObj | null>(props.selectedOption || null)
    const [selectedOptions, setSelectedOptions] = useState<DropdownObj[]>(props.selectedOptions || [])

    const [optionsContainerWidth, setOptionsContainerWidth] = useState(0)

    const searchInputRef = useRef<HTMLInputElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)

    const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null)

    const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
        setOptionsContainerWidth(event.currentTarget.clientWidth)
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const open = Boolean(anchorEl)
    const id = open ? "virtualized-search-select" : undefined

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
        if (scrollTop + clientHeight >= scrollHeight - 50 && !props.loading) {
            throttledLoadMore()
        }
    }

    const onLoadMoreRef = useRef(props.onLoadMore)
    useEffect(() => {
        onLoadMoreRef.current = props.onLoadMore
    }, [props.onLoadMore])

    const throttledLoadMore = useRef(throttle(() => onLoadMoreRef.current(), 500)).current
    const debouncedSearch = useMemo(() => debounce(props.onSearch, 500), [props.onSearch])

    const handleOptionClick = (option: DropdownObj) => {
        if (props.multiSelect) {
            const current = props.selectedOptions ?? selectedOptions
            const exists = current.findIndex(o => o.id === option.id) > -1
            const updated = exists ? current.filter(o => o.id !== option.id) : [...current, option]
            setSelectedOptions(updated)
            props.onMultiChange?.(updated)
        } else {
            setSelectedOption(option)
            props.onChange?.(option)
            handleClose()
        }
    }

    const handleRemoveChip = (option: DropdownObj, e: React.MouseEvent) => {
        e.stopPropagation()
        const current = props.selectedOptions ?? selectedOptions
        const updated = current.filter(o => o.id !== option.id)
        setSelectedOptions(updated)
        props.onMultiChange?.(updated)
    }

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (showOptions && containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setShowOptions(false)
                debouncedSearch("")
            }
        }
        document.addEventListener("mousedown", handleClickOutside)
        return () => {
            document.removeEventListener("mousedown", handleClickOutside)
        }
    }, [showOptions])

    useEffect(() => {
        setSelectedOption(props.selectedOption || null)
    }, [props.selectedOption])

    useEffect(() => {
        setSelectedOptions(props.selectedOptions || [])
    }, [props.selectedOptions])

    useEffect(() => {
        if (searchInputRef.current) {
            searchInputRef.current.focus()
        }
    }, [showOptions])

    const renderTrigger = () => {
        if (!props.multiSelect) {
            return (
                <span style={{ fontSize: props?.fontSize }}>
                    {selectedOption ? selectedOption.title : props?.placeholder || "Select an option"}
                </span>
            )
        }
        if (selectedOptions.length === 0) {
            return (
                <span style={{ fontSize: props?.fontSize, color: "#9e9e9e" }}>
                    {props?.placeholder || "Select options"}
                </span>
            )
        }
        return (
            <div className={styles.chipsWrapper} onClick={e => e.stopPropagation()}>
                {selectedOptions.map(opt => (
                    <span key={opt.id} className={styles.chip} title={opt.title}>
                        <span className={styles.chipLabel}>{opt.title}</span>
                        <span className={styles.chipRemove} onClick={e => handleRemoveChip(opt, e)}>
                            &times;
                        </span>
                    </span>
                ))}
            </div>
        )
    }

    return (
        <div
            className={`${styles.virtualizedSearchSelect} ${props.className}`}
            style={{ height: props?.height }}
            ref={containerRef}>
            <div
                className={`d-flex justify-content-between align-items-center p-5 cursor-pointer ${
                    styles.selectedOption
                } ${props.showError && !!props.error ? styles.error : ""}`}
                onClick={event => !props.disabled && handleClick(event)}>
                {renderTrigger()}
                <img
                    src={downArrow}
                    alt="down arrow"
                    className={showOptions ? styles.open : ""}
                    style={{ flexShrink: 0 }}
                />
            </div>
            {props.showError && !!props.error && <div className={styles.errorText}>{props.error}</div>}
            <Popover
                id={id}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slotProps={{
                    paper: {
                        onScroll: handleScroll,
                    },
                }}
                sx={{
                    height: "250px",
                }}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}>
                <Box
                    sx={{ width: optionsContainerWidth }}
                    className={`${props.options?.length === 0 ? styles.noOptions : ""} ${styles.optionsContainer}`}>
                    <InputTag
                        type="text"
                        className="w-100"
                        placeholder={props?.loading ? `Loading...` : "Search..."}
                        onChange={e => debouncedSearch(e.target.value)}
                        ref={searchInputRef}
                    />
                    {props.options?.length === 0 ? (
                        <div className="text-xs p-5">No Options...</div>
                    ) : (
                        <Box sx={{ height: "100%" }} className={styles.optionsList}>
                            {props.options.map((option, index) => {
                                const isChecked = props.multiSelect
                                    ? selectedOptions.findIndex(o => o.id === option.id) > -1
                                    : false
                                return (
                                    <Box
                                        key={index}
                                        sx={{
                                            padding: "8px 12px",
                                            cursor: "pointer",
                                            fontSize: "10px",
                                            color: "#101941",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "6px",
                                            "&:hover": {
                                                borderLeft: "2px solid #70758d",
                                                backgroundColor: "#e8e8ec",
                                            },
                                        }}
                                        className={styles.optionItem}
                                        onClick={() => handleOptionClick(option)}>
                                        {props.multiSelect && (
                                            <input
                                                type="checkbox"
                                                checked={isChecked}
                                                onChange={() => {}}
                                                style={{ cursor: "pointer", flexShrink: 0 }}
                                            />
                                        )}
                                        {option.title}
                                    </Box>
                                )
                            })}
                        </Box>
                    )}
                </Box>
            </Popover>
        </div>
    )
}

export default VirtualizedSearchSelect
